import { useCallback, useEffect, useState } from "react";
import { Ban, Copy, KeyRound, Pencil, ShieldAlert, UserCheck, UserCog, Users as UsersIcon } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import LoadFailureNote from "./LoadFailureNote";
import AdminCreateUserForm from "./AdminCreateUserForm";
import AdminUserLinksSection from "./AdminUserLinksSection";
import AdminEditUserDialog from "./AdminEditUserDialog";
import AdminSetDobDialog from "./AdminSetDobDialog";
import api, { formatApiErrorDetail } from "../lib/api";
// #569: the set-DOB dialog must agree with the sign-up form about what a
// usable date is and where the guardian threshold sits — one function, one
// number, imported rather than re-derived.
import { ageFromDob, GUARDIAN_HELD_BELOW_AGE } from "../lib/dateOfBirth";

/**
 * Admin account provisioning (issue #226) — the body of the Users section
 * of `/admin` (`pages/AdminUsers.jsx`). Slice 2 rendered it inline on the
 * admin Dashboard because `/admin` did not exist yet; the panel itself is
 * unchanged by the move, which is the point of it owning its own fetching
 * and state.
 *
 * This is an internal tool for one or two people, so it is deliberately
 * plain: no pagination, no search, no bulk import. What it is NOT plain
 * about is the one-time password, because that is the only part of this
 * screen where the app can silently lose something a human needs:
 *
 *   - `POST /admin/users` returns the generated password ONCE. It is not
 *     stored in plaintext anywhere and `GET /admin/users` has no field
 *     that could return it, so if this panel drops it, the only recovery
 *     is a password reset. The credential therefore renders in a panel
 *     that stays up until it is explicitly dismissed — it is never a
 *     toast (they auto-dismiss), and never inside the create form (which
 *     clears on submit).
 *   - The panel says "this will not be shown again" in those words, next
 *     to the password, before the admin navigates away.
 *   - Copy is an affordance, not the only route: the password also renders
 *     as selectable text, because `navigator.clipboard` is unavailable on
 *     a non-secure origin and rejects when the document isn't focused.
 *     A copy button that silently fails would lose the credential exactly
 *     as thoroughly as not showing it.
 *
 * Failure shows failure (#139's rule): no create/reset path here reaches
 * `toast.success` unless the API actually returned 2xx, and a failed list
 * load renders `LoadFailureNote` rather than an empty roster, which would
 * be a claim that the platform has no accounts (#271).
 */

const ROLE_LABEL = { player: "Player", coach: "Coach", admin: "Admin" };

/**
 * The roles an admin may grant or revoke here (#507) — mirrors the API's
 * `GrantableRole`. `admin` is absent for the same reason the create form
 * can't mint one: admin-mints-admin is the escalation path the API refuses,
 * and this screen doesn't render a button whose only outcome is a 400.
 */
const GRANTABLE_ROLES = ["player", "coach"];

/** The server's own 409 text for revoking a default role — shown as the
 * DISABLED state's reason instead of offered-then-errored. */
const DEFAULT_ROLE_LOCKED = "An account's default role cannot be removed";

const EMPTY_FORM = { display_name: "", email: "", role: "player", date_of_birth: "" };

function fmtWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-NZ");
}

/**
 * The one-time credential. `subject` names who it belongs to, so an admin
 * provisioning several people in a row cannot hand the wrong password to
 * the wrong person.
 */
function OneTimePasswordPanel({ issued, onDismiss }) {
  const copy = async () => {
    try {
      if (!navigator?.clipboard?.writeText) throw new Error("clipboard unavailable");
      await navigator.clipboard.writeText(issued.password);
      toast.success("Password copied to the clipboard");
    } catch {
      // Never claim a copy that didn't happen — the password is still on
      // screen and selectable, which is why this is a warning and not an
      // error the admin has to act on.
      toast.warning("Couldn't copy automatically — select the password and copy it manually");
    }
  };

  return (
    <Card className="border-2" style={{ borderColor: "#D4A017" }} data-testid="admin-otp-panel">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-start gap-2 min-w-0">
          <KeyRound size={18} color="#D4A017" className="mt-0.5 shrink-0" />
          <div className="min-w-0">
            <h3 className="font-display font-bold text-slate-900 break-words">
              {issued.kind === "reset" ? "New password for " : "Password for "}
              {issued.subject}
            </h3>
            <p className="text-sm font-semibold text-red-700 mt-1 break-words" data-testid="admin-otp-warning">
              Copy this now — it will not be shown again.
            </p>
            <p className="text-xs text-slate-500 mt-1 break-words">
              We can't email it (there's no mail sending yet), so pass it on yourself. If it's lost, the only
              way back is another reset.
            </p>
          </div>
        </div>

        <div
          className="rounded-lg bg-slate-900 text-white px-3 py-2 font-mono text-sm select-all break-all"
          data-testid="admin-otp-value"
        >
          {issued.password}
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <Button onClick={copy} className="w-full sm:w-auto" style={{ background: "#0B2E59" }} data-testid="admin-otp-copy">
            <Copy size={14} className="mr-2" /> Copy password
          </Button>
          <Button onClick={onDismiss} variant="outline" className="w-full sm:w-auto" data-testid="admin-otp-dismiss">
            I've saved it
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminUsersPanel() {
  const [users, setUsers] = useState([]);
  const [loadFailed, setLoadFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [resettingId, setResettingId] = useState(null);
  // #507: the row whose role controls are mid-request, so a double-click
  // can't race two grants.
  const [roleBusyId, setRoleBusyId] = useState(null);
  // #567: the row whose disable/enable is mid-request — same double-click
  // guard as roleBusyId.
  const [disableBusyId, setDisableBusyId] = useState(null);
  // `{ password, subject, kind }` — the only place a generated credential
  // lives in this app, and only until dismissed or replaced.
  const [issued, setIssued] = useState(null);
  // #569: the unbanded roster row whose date of birth is being entered, plus
  // the dialog's own field state. One row at a time — the dialog is modal.
  const [dobTarget, setDobTarget] = useState(null);
  const [dobValue, setDobValue] = useState("");
  const [dobError, setDobError] = useState("");
  const [dobSaving, setDobSaving] = useState(false);
  // #566: the roster row whose name/email is being corrected, plus the
  // dialog's own field state. Same one-modal-at-a-time shape as the DOB
  // dialog above.
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ display_name: "", email: "" });
  const [editError, setEditError] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  // #568: per-account coach-link view. Keyed by USER id (the roster's own
  // key), lazily loaded on first expand — `{ open, loading, failed, links }`.
  // Read-only + one revoke button per link; creation stays party-initiated
  // (#288), so nothing here creates a link.
  const [linksByUser, setLinksByUser] = useState({});
  // The link mid-revoke, so a double-click can't fire two DELETEs.
  const [revokingLinkId, setRevokingLinkId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/admin/users")
      .then((r) => {
        if (cancelled) return;
        setUsers(r.data || []);
        setLoadFailed(false);
      })
      .catch(() => {
        // #271: do NOT substitute an empty list for a missing answer — an
        // empty roster here reads as "nobody has an account".
        if (!cancelled) setLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const setField = useCallback((key, value) => setForm((f) => ({ ...f, [key]: value })), []);

  const canSubmit = form.display_name.trim() && form.email.trim() && !submitting;

  // #300 — see the roster below for what this is for.
  const pendingCount = users.filter((u) => u.must_change_password).length;

  const createUser = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload = {
        display_name: form.display_name.trim(),
        email: form.email.trim(),
        role: form.role,
        ...(form.date_of_birth ? { date_of_birth: form.date_of_birth } : {}),
      };
      const { data } = await api.post("/admin/users", payload);
      // Show the credential BEFORE anything else can go wrong — a failed
      // list refresh must not cost the admin the password.
      setIssued({ password: data.one_time_password, subject: `${data.display_name} (${data.email})`, kind: "create" });
      setUsers((current) => [
        {
          id: data.id,
          email: data.email,
          display_name: data.display_name,
          role: data.role,
          created_at: data.created_at,
          // The create response is the account, not the roster row, so it
          // carries no `age_banded` — derive the same value the roster
          // would report rather than leaving the new row unmarked until
          // the next reload (which is precisely when an unbanded junior
          // would be easiest to miss).
          age_banded: data.role === "player" ? Boolean(payload.date_of_birth) : null,
          // #300 — from the response, not hardcoded `true`. The server is
          // what decides whether a minted credential is still owed a
          // change; a client that assumed it would keep saying so long
          // after the server stopped.
          must_change_password: data.must_change_password,
          // #507 — same rule: the 201 states the account's role set, so
          // the chips on this fresh row come from the wire like every
          // other row's, never from the client guessing.
          roles: data.roles,
        },
        ...current,
      ]);
      setForm(EMPTY_FORM);
      toast.success(`${data.display_name} can now sign in`);
    } catch (err) {
      const status = err.response?.status;
      const detail = formatApiErrorDetail(err.response?.data?.detail);
      if (status === 409) {
        toast.error(detail || "That email address already has an account");
      } else {
        toast.error(detail || "Could not create the account. Please try again.");
      }
    }
    setSubmitting(false);
  };

  const resetPassword = async (user) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`Reset the password for ${user.display_name}? Their current password stops working immediately.`)) {
      return;
    }
    setResettingId(user.id);
    try {
      const { data } = await api.post(`/admin/users/${user.id}/reset-password`, {});
      setIssued({ password: data.one_time_password, subject: `${data.display_name} (${data.email})`, kind: "reset" });
      // #300: a reset re-opens the window the marker exists to show — this
      // admin has just seen a credential again — so the row must go back to
      // "password not changed yet" without waiting for a reload the admin
      // has no reason to trigger.
      setUsers((current) =>
        current.map((u) => (u.id === user.id ? { ...u, must_change_password: data.must_change_password } : u)),
      );
      toast.success(`New password issued for ${data.display_name}`);
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail);
      toast.error(detail || "Could not reset the password. Please try again.");
    }
    setResettingId(null);
  };

  /**
   * #567 — reversible deactivation. Disable stops every sign-in door and
   * kills open sessions on their next request; enable restores everything
   * (same password — nothing is rotated, nothing deleted). The API refuses
   * admin targets independently (403) — this screen never offers the
   * affordance on an admin row, the standing convention.
   *
   * On success, merge ONLY `disabled_at` into the existing row (the
   * mergeRoles rule): the response is the full roster row, but this
   * handler owns exactly one field, and a wholesale swap would erase the
   * #300 password-pending marker the moment the shapes drift.
   */
  const setAccountDisabled = async (user, disabled) => {
    const message = disabled
      ? `Disable ${user.display_name}'s account? They are signed out everywhere and can't sign in again until it's enabled. Nothing is deleted — their matches, goals and coach links all stay.`
      : `Enable ${user.display_name}'s account? They can sign in again with the password they already had.`;
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(message)) return;
    setDisableBusyId(user.id);
    try {
      const { data } = await api.post(`/admin/users/${user.id}/${disabled ? "disable" : "enable"}`, {});
      setUsers((current) =>
        current.map((u) => (u.id === user.id ? { ...u, disabled_at: data.disabled_at } : u)),
      );
      toast.success(
        disabled
          ? `${user.display_name}'s account is disabled — nothing was deleted`
          : `${user.display_name} can sign in again`,
      );
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail);
      toast.error(detail || `Could not ${disabled ? "disable" : "enable"} the account. Please try again.`);
    }
    setDisableBusyId(null);
  };

  /**
   * #507 grant/revoke. The response is the ACCOUNT shape plus `roles` — it
   * carries no `must_change_password`, `date_of_birth` or `age_banded` —
   * so on success ONLY `roles` is merged into the existing row. Replacing
   * the row wholesale would silently erase the #300 password-pending
   * marker and the age-unknown marker until the next full reload.
   */
  const mergeRoles = (id, roles) =>
    setUsers((current) => current.map((u) => (u.id === id ? { ...u, roles } : u)));

  const addRole = async (user, role) => {
    setRoleBusyId(user.id);
    try {
      const { data } = await api.post(`/admin/users/${user.id}/roles`, { role });
      mergeRoles(user.id, data.roles);
      // Truthful: granting revokes nothing — the API touches no session,
      // and the holder switches hats from their own account menu.
      toast.success(
        `${user.display_name} can now also ${role === "coach" ? "coach" : "play"}. Nobody is signed out — they switch roles from their account menu.`,
      );
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail);
      toast.error(detail || "Could not add the role. Please try again.");
    }
    setRoleBusyId(null);
  };

  const removeRole = async (user, role) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`Remove the ${ROLE_LABEL[role].toLowerCase()} role from ${user.display_name}? Any device using it drops back to ${ROLE_LABEL[user.role].toLowerCase()} on its next action.`)) {
      return;
    }
    setRoleBusyId(user.id);
    try {
      const { data } = await api.delete(`/admin/users/${user.id}/roles/${role}`);
      mergeRoles(user.id, data.roles);
      toast.success(`${ROLE_LABEL[role]} role removed from ${user.display_name}`);
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail);
      toast.error(detail || "Could not remove the role. Please try again.");
    }
    setRoleBusyId(null);
  };

  const openDobDialog = (user) => {
    setDobTarget(user);
    setDobValue("");
    setDobError("");
  };

  const openEditDialog = (user) => {
    setEditTarget(user);
    setEditForm({ display_name: user.display_name, email: user.email });
    setEditError("");
  };

  const updateEditField = (key, value) => {
    setEditForm((f) => ({ ...f, [key]: value }));
    setEditError("");
  };

  /**
   * #566 — correct an account's display name and/or email.
   *
   * The payload carries ONLY the fields that actually changed: the API's
   * schema is strict and requires at least one, and re-sending an
   * unchanged value as if it were an edit would make "what did the admin
   * change?" unanswerable from the request. If nothing changed, close the
   * dialog without a request at all.
   *
   * On success, merge-not-replace (the `mergeRoles` rule): only
   * `display_name` and `email` change on the row, so the #300
   * password-pending marker, the age-unknown marker, and the #507 role
   * chips all survive without a reload. The response is the full roster
   * row, but this handler still merges only the fields this feature owns —
   * a wholesale swap is the exact bug the source tests below pin against.
   *
   * A 409 (email already on another account) renders as a field error IN
   * the dialog, next to the input that caused it, so the admin can fix the
   * address without re-opening anything. The API refuses admin targets
   * independently (403) — this screen never offers the affordance on an
   * admin row, same convention as reset.
   */
  const saveEdit = async () => {
    if (!editTarget) return;
    const display_name = editForm.display_name.trim();
    const email = editForm.email.trim();
    if (!display_name) {
      setEditError("A display name is required.");
      return;
    }
    if (!email) {
      setEditError("An email address is required.");
      return;
    }
    const payload = {};
    if (display_name !== editTarget.display_name) payload.display_name = display_name;
    // The API lowercases on write; compare case-insensitively so retyping
    // the same address with different casing isn't sent as a change.
    if (email.toLowerCase() !== editTarget.email) payload.email = email;
    if (Object.keys(payload).length === 0) {
      setEditTarget(null);
      return;
    }
    setEditSaving(true);
    try {
      const { data } = await api.patch(`/admin/users/${editTarget.id}`, payload);
      setUsers((current) =>
        current.map((u) =>
          u.id === editTarget.id ? { ...u, display_name: data.display_name, email: data.email } : u,
        ),
      );
      toast.success(`Account details updated for ${data.display_name}`);
      setEditTarget(null);
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail);
      if (err.response?.status === 409) {
        setEditError(detail || "That email address already has an account");
      } else {
        setEditError(detail || "Could not save the changes. Please try again.");
      }
    }
    setEditSaving(false);
  };

  /**
   * #569 — the after-creation half of the create form's optional DOB.
   *
   * The roster row is keyed by USER id, but the write path
   * (`PUT /api/players/:id` — the admin correction path that route now
   * documents and `api/test/players.test.ts` pins) is keyed by
   * player-profile id, which the roster deliberately doesn't carry. The
   * admin's `GET /api/players` returns every profile with its `user_id`,
   * so the profile is resolved right before the write instead of teaching
   * the roster shape a second id.
   *
   * Client-side validation is `ageFromDob` — the sign-up form's own rule,
   * same module — so a date the API would 400 is refused here with a field
   * error instead of a toast round-trip; the API still re-checks it
   * (`lib/dateOfBirth.ts`).
   *
   * On success, merge-not-replace (same rule as `mergeRoles`): only
   * `date_of_birth` and `age_banded` change, so the #300 password-pending
   * marker survives. `age_banded: true` is exactly what the roster would
   * now report — a stored DOB is what "banded" means (see
   * `toApiRosterUser`, and #552 for which rows carry age fields at all).
   */
  const saveDob = async () => {
    if (!dobTarget) return;
    if (ageFromDob(dobValue) === null) {
      setDobError("Enter the player's date of birth as a real date in the past.");
      return;
    }
    setDobSaving(true);
    try {
      const { data: profiles } = await api.get("/players");
      const profile = (profiles || []).find((p) => p.user_id === dobTarget.id);
      if (!profile) {
        toast.error(`No player profile found for ${dobTarget.display_name} — reload the page and try again.`);
      } else {
        const { data } = await api.put(`/players/${profile.player_id}`, { date_of_birth: dobValue });
        setUsers((current) =>
          current.map((u) =>
            u.id === dobTarget.id ? { ...u, date_of_birth: data.date_of_birth, age_banded: true } : u,
          ),
        );
        toast.success(`Date of birth saved for ${dobTarget.display_name}`);
        setDobTarget(null);
      }
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail);
      toast.error(detail || "Could not save the date of birth. Please try again.");
    }
    setDobSaving(false);
  };

  /**
   * #568 — the account's coach↔player links, loaded on first expand from
   * `GET /admin/users/:id/links` (admin-only, behind the module's role
   * gate). Read-only: this section only ever REVOKES an existing link
   * (below); it never creates one — link creation stays party-initiated
   * (#288/#506), and this screen deliberately offers no create affordance.
   *
   * A failed load renders its own note (the #271/#139 rule — never a silent
   * empty list that reads as "no links"). Cached after the first load, so
   * re-expanding a row is instant and the toggle just flips `open`.
   */
  const fetchLinks = async (user) => {
    setLinksByUser((m) => ({ ...m, [user.id]: { open: true, loading: true, failed: false, links: null } }));
    try {
      const { data } = await api.get(`/admin/users/${user.id}/links`);
      setLinksByUser((m) => ({ ...m, [user.id]: { open: true, loading: false, failed: false, links: data || [] } }));
    } catch {
      // #271: a failed load must show failure, never a silent empty list
      // that would read as "this account has no links".
      setLinksByUser((m) => ({ ...m, [user.id]: { open: true, loading: false, failed: true, links: null } }));
    }
  };

  const toggleLinks = (user) => {
    const current = linksByUser[user.id];
    if (current?.open) {
      setLinksByUser((m) => ({ ...m, [user.id]: { ...current, open: false } }));
      return;
    }
    // Cached from a previous expand — just re-open, no refetch.
    if (current?.links) {
      setLinksByUser((m) => ({ ...m, [user.id]: { ...current, open: true } }));
      return;
    }
    fetchLinks(user);
  };

  /**
   * #568 revoke — the EXISTING `DELETE /api/coach-links/:id`, already
   * admin-authorized (coachLinks.ts). The confirm names BOTH parties so an
   * admin cannot revoke the wrong pairing off a row full of them. On
   * success the link is removed from THIS account's cached list
   * (merge-not-replace: only that account's `links` array changes; every
   * other row and marker is untouched).
   */
  const revokeLink = async (user, link) => {
    const counterpartWord = link.counterpart_role === "coach" ? "coach" : "player";
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`Revoke the link between ${user.display_name} and their ${counterpartWord} ${link.counterpart_display_name}? ${link.counterpart_display_name} loses access on their next load. Either party can re-create it later — this screen can't.`)) {
      return;
    }
    setRevokingLinkId(link.id);
    try {
      await api.delete(`/coach-links/${link.id}`);
      setLinksByUser((m) => {
        const entry = m[user.id];
        if (!entry?.links) return m;
        return { ...m, [user.id]: { ...entry, links: entry.links.filter((l) => l.id !== link.id) } };
      });
      toast.success(`Link between ${user.display_name} and ${link.counterpart_display_name} revoked`);
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail);
      toast.error(detail || "Could not revoke the link. Please try again.");
    }
    setRevokingLinkId(null);
  };

  // Live while the dialog is open: the guardian note appears the moment the
  // typed date puts the player under the threshold, exactly as the sign-up
  // form behaves.
  const dobAge = dobTarget ? ageFromDob(dobValue) : null;
  const dobGuardianHeld = dobAge !== null && dobAge < GUARDIAN_HELD_BELOW_AGE;

  return (
    <div className="space-y-4" data-testid="admin-users-panel">
      <div className="flex items-center gap-2">
        <UsersIcon size={16} color="#0B2E59" />
        <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Accounts</p>
      </div>

      {issued && <OneTimePasswordPanel issued={issued} onDismiss={() => setIssued(null)} />}

      <AdminCreateUserForm
        form={form}
        onFieldChange={setField}
        onSubmit={createUser}
        submitting={submitting}
        canSubmit={canSubmit}
      />

      {/* Roster */}
      {loadFailed ? (
        <Card className="border-slate-200">
          <CardContent className="p-5 flex">
            <LoadFailureNote
              what="the account list"
              onRetry={() => setReloadKey((k) => k + 1)}
              testId="admin-users-failed"
            />
          </CardContent>
        </Card>
      ) : users.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="p-12 text-center" data-testid="admin-users-empty">
            <p className="text-slate-500">No accounts yet.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="admin-users-list">
          {/* #300's actual operational question, answered without making
              the admin scan every row: "how many people still haven't set
              their own password?" Derived from the rows already loaded —
              no extra request, and it cannot disagree with the markers
              below because it is counting them. Hidden at zero rather than
              rendered as "0 accounts" — nothing to chase is not news. */}
          {pendingCount > 0 && (
            <p className="text-xs text-amber-700 break-words" data-testid="admin-pwpending-count">
              {pendingCount === 1
                ? "1 account is still using the password it was given."
                : `${pendingCount} accounts are still using the password they were given.`}
            </p>
          )}
          {users.map((u) => (
            <Card
              key={u.id}
              // #567: a disabled row must not look like a working account —
              // muted ground and border, plus the badge and marker below.
              className={u.disabled_at ? "border-slate-300 bg-slate-100" : "border-slate-200"}
              data-testid={`admin-user-${u.id}`}
            >
              <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-slate-900 break-words">{u.display_name}</span>
                    {/* #507: chips render the WIRE role set, never a set
                        guessed from `role`. The `?? [u.role]` fallback is
                        the server's own invariant (the default role is
                        always in the set), not a client-side opinion. */}
                    {(u.roles ?? [u.role]).map((role) => (
                      <Badge key={role} variant="outline" data-testid={`admin-role-chip-${role}-${u.id}`}>
                        {ROLE_LABEL[role] || role}
                      </Badge>
                    ))}
                    {u.disabled_at && (
                      <Badge
                        variant="outline"
                        className="border-red-300 text-red-700"
                        data-testid={`admin-disabled-badge-${u.id}`}
                      >
                        Disabled
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5 break-all">{u.email}</div>
                  {/* `age_banded === false` means: this is a player account
                      with no date of birth, so nothing can work out how old
                      they are. Marked rather than left silent — an unbanded
                      account that looks identical to a banded one is how a
                      junior ends up treated as an adult by default. `null`
                      is "not applicable" (coach/admin), not "unknown". */}
                  {u.age_banded === false && (
                    <div
                      className="text-[11px] mt-1 flex items-center gap-1 text-amber-700 break-words"
                      data-testid={`admin-unbanded-${u.id}`}
                    >
                      <ShieldAlert size={11} className="shrink-0" />
                      <span>Age unknown — no date of birth on file</span>
                      {/* #569: the fix for the marker, on the marker. PLAYER
                          rows only, per this screen's convention that admin
                          rows get no controls. Until #552 lands, `age_banded
                          === false` only ever arrives on player-default rows
                          (the roster gates age fields on `role`); once #552
                          gates them on player capability instead, unbanded
                          dual-role rows grow this same affordance with no
                          change here. */}
                      {u.role !== "admin" && (
                        <button
                          type="button"
                          className="underline font-semibold text-amber-800 hover:text-amber-900 shrink-0"
                          onClick={() => openDobDialog(u)}
                          data-testid={`admin-set-dob-${u.id}`}
                        >
                          Set date of birth
                        </button>
                      )}
                    </div>
                  )}
                  {/* #300: who is still holding a password an admin typed
                      in. #295 forces the change and confines the session
                      until it happens, but until now nothing SHOWED it, so
                      the one person who could chase the stragglers had to
                      read the database to find them. Rendered in the same
                      shape as "Age unknown" above and immediately below it
                      — an account can legitimately be both, and at 390px
                      each is its own wrapped line rather than a widening
                      row (#251). Not a credential: it says a change is
                      owed, never what the password is. */}
                  {u.must_change_password && (
                    <div
                      className="text-[11px] mt-1 flex items-center gap-1 text-amber-700 break-words"
                      data-testid={`admin-pwpending-${u.id}`}
                    >
                      <KeyRound size={11} className="shrink-0" />
                      <span>Password not changed yet — still using the one they were given</span>
                    </div>
                  )}
                  {/* #567: when and that the account is disabled — the row
                      stays listed (this screen is the only way back in),
                      but says plainly that sign-in is off and data kept. */}
                  {u.disabled_at && (
                    <div
                      className="text-[11px] mt-1 flex items-center gap-1 text-red-700 break-words"
                      data-testid={`admin-disabled-${u.id}`}
                    >
                      <Ban size={11} className="shrink-0" />
                      <span>Disabled {fmtWhen(u.disabled_at)} — can't sign in; all their data is kept</span>
                    </div>
                  )}
                  {u.created_at && (
                    <div className="text-[11px] text-slate-400 mt-0.5">Created {fmtWhen(u.created_at)}</div>
                  )}
                  {/* #507 role controls. Admin rows get NONE — an admin's
                      only role is `admin`, which this screen can neither
                      grant nor revoke (same reasoning as the reset note on
                      the right). For everyone else: the default role is
                      shown locked with the API's own 409 reason instead of
                      offered-then-errored; a held second role gets a
                      remove control; a missing one gets an add control.
                      Granting signs nobody out — the holder switches roles
                      from their account menu. */}
                  {u.role === "admin" ? (
                    <div
                      className="text-[11px] text-slate-400 mt-2 flex items-center gap-1 break-words"
                      data-testid={`admin-roles-note-${u.id}`}
                    >
                      <ShieldAlert size={11} className="shrink-0" />
                      <span>Admin — roles not managed here</span>
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {GRANTABLE_ROLES.map((role) => {
                        if (role === u.role) {
                          return (
                            <Button
                              key={role}
                              variant="outline"
                              size="sm"
                              disabled
                              title={DEFAULT_ROLE_LOCKED}
                              data-testid={`admin-role-default-${u.id}`}
                            >
                              <UserCog size={12} className="mr-1.5" />
                              {ROLE_LABEL[role]} is their default role
                            </Button>
                          );
                        }
                        const held = (u.roles ?? [u.role]).includes(role);
                        return (
                          <Button
                            key={role}
                            variant="outline"
                            size="sm"
                            disabled={roleBusyId === u.id}
                            onClick={() => (held ? removeRole(u, role) : addRole(u, role))}
                            data-testid={`admin-role-${held ? "revoke" : "grant"}-${role}-${u.id}`}
                          >
                            <UserCog size={12} className="mr-1.5" />
                            {held ? `Remove ${ROLE_LABEL[role].toLowerCase()} role` : `Add ${ROLE_LABEL[role].toLowerCase()} role`}
                          </Button>
                        );
                      })}
                    </div>
                  )}
                  {/* #568: read-only coach↔player links, NON-ADMIN rows only
                      — an admin is a party to none, so the section is omitted
                      entirely (the no-controls idiom this screen already uses
                      for role/reset/disable on admin rows). Lazily loaded on
                      expand; each link gets a Revoke button wired to the
                      EXISTING DELETE /coach-links/:id. Link CREATION is NOT
                      offered here — it stays party-initiated (#288/#506). */}
                  {u.role !== "admin" && (
                    <AdminUserLinksSection
                      u={u}
                      linksState={linksByUser[u.id]}
                      onToggle={() => toggleLinks(u)}
                      onRetry={() => fetchLinks(u)}
                      onRevoke={(link) => revokeLink(u, link)}
                      revokingLinkId={revokingLinkId}
                    />
                  )}
                </div>
                {u.role === "admin" ? (
                  // The API refuses both (403) — saying so here is kinder
                  // than buttons that always fail. Resetting an admin's
                  // password is an account-takeover path, and rewriting
                  // their email is the same move through any email-keyed
                  // recovery (#566), so admin rows get NO controls.
                  <span className="text-xs text-slate-400 flex items-center gap-1 shrink-0">
                    <ShieldAlert size={12} /> Admin — no edits, resets, or disabling from this screen
                  </span>
                ) : (
                  /* #621 — `lg:flex-row`, not `sm:flex-row`. Three buttons
                     side by side measure 455px, and this column is
                     `shrink-0` (rightly: a half-width "Reset password" is
                     not a control). From 640px up, the app also has a
                     SIDEBAR, so the row is laid out in 397px of card at
                     tablet-portrait-768 and the last button landed 23px off
                     the right edge of the page. The band where `sm:` was
                     wrong is exactly 640-1023px: below it the sidebar is
                     gone and the buttons are already stacked, above it there
                     is room for the row. Found by the responsive harness's
                     full four-viewport run; the merge gate runs phone-390
                     only, where this never bit. */
                  <div className="flex flex-col lg:flex-row gap-2 shrink-0">
                    {/* #566: name/email corrections, non-admin rows only. */}
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full sm:w-auto"
                      onClick={() => openEditDialog(u)}
                      data-testid={`admin-edit-${u.id}`}
                    >
                      <Pencil size={12} className="mr-1.5" />
                      Edit details
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full sm:w-auto"
                      disabled={resettingId === u.id}
                      onClick={() => resetPassword(u)}
                      data-testid={`admin-reset-${u.id}`}
                    >
                      <KeyRound size={12} className="mr-1.5" />
                      {resettingId === u.id ? "Resetting…" : "Reset password"}
                    </Button>
                    {/* #567: one toggle, labelled by the row's wire state. */}
                    <Button
                      variant="outline"
                      size="sm"
                      className={`w-full sm:w-auto ${u.disabled_at ? "" : "text-red-700 border-red-200 hover:bg-red-50"}`}
                      disabled={disableBusyId === u.id}
                      onClick={() => setAccountDisabled(u, !u.disabled_at)}
                      data-testid={`admin-${u.disabled_at ? "enable" : "disable"}-${u.id}`}
                    >
                      {u.disabled_at ? (
                        <UserCheck size={12} className="mr-1.5" />
                      ) : (
                        <Ban size={12} className="mr-1.5" />
                      )}
                      {disableBusyId === u.id
                        ? "Working…"
                        : u.disabled_at
                          ? "Enable account"
                          : "Disable account"}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* #566: name/email correction dialog, rendered once for whichever
          row opened it. Sessions are untouched by an email edit (the API
          revokes nothing — email is identity, not a credential), and the
          copy says so, because an admin who believes an edit signs the
          person out will "helpfully" follow it with a needless reset. */}
      <AdminEditUserDialog
        editTarget={editTarget}
        editForm={editForm}
        editError={editError}
        editSaving={editSaving}
        onOpenChange={(open) => { if (!open) setEditTarget(null); }}
        onFieldChange={updateEditField}
        onCancel={() => setEditTarget(null)}
        onSave={saveEdit}
      />

      {/* #569: one date field, writing through the pinned admin path on
          PUT /api/players/:id. Rendered once for whichever row opened it. */}
      <AdminSetDobDialog
        dobTarget={dobTarget}
        dobValue={dobValue}
        dobError={dobError}
        dobSaving={dobSaving}
        dobGuardianHeld={dobGuardianHeld}
        onOpenChange={(open) => { if (!open) setDobTarget(null); }}
        onValueChange={(value) => { setDobValue(value); setDobError(""); }}
        onCancel={() => setDobTarget(null)}
        onSave={saveDob}
      />
    </div>
  );
}
